import React, { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "../../../shared/ui/Modal";
import Button from "../../../shared/ui/Button";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { JamedaLogo } from "../platformCatalog";
import { useTranslation } from "../../../i18n";
import { connectJameda } from "../jamedaApi";
import { useRequestMoreProfiles } from "./platforms/PlanLimitNotice";

const GUIDE_STEP_KEYS = [
  { titleKey: "jamedaConnect.step1Title", detailKey: "jamedaConnect.step1Detail", icon: "key" },
  { titleKey: "jamedaConnect.step2Title", detailKey: "jamedaConnect.step2Detail", icon: "hub" },
  { titleKey: "jamedaConnect.step3Title", detailKey: "jamedaConnect.step3Detail", icon: "content_copy" },
  { titleKey: "jamedaConnect.step4Title", detailKey: "jamedaConnect.step4Detail", icon: "link" },
];

/**
 * Credentials-based Jameda connection modal.
 * Left: step-by-step guide showing where to find the Business ID + API key
 * inside the Jameda account. Right: the actual form.
 *
 * @param {{ open: boolean, onClose: () => void, onConnected: (result: object) => void }} props
 */
const JamedaConnectModal = ({ open, onClose, onConnected }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [businessId, setBusinessId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Server refused a SECOND profile on this platform (Basic/Premium limit).
  const [limitHit, setLimitHit] = useState(false);
  const requestMoreProfiles = useRequestMoreProfiles();

  useEffect(() => {
    if (open) {
      setBusinessId("");
      setApiKey("");
      setShowKey(false);
      setBusy(false);
      setError("");
      setLimitHit(false);
    }
  }, [open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!businessId.trim() || !apiKey.trim()) {
      setError(t("jamedaConnect.errorRequired"));
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await connectJameda({
        businessId: businessId.trim(),
        apiKey: apiKey.trim(),
      });
      onConnected?.({ ...result, businessId: businessId.trim() });
    } catch (err) {
      const code = err?.data?.code;
      if (code === "plan_limit") {
        setLimitHit(true);
        setError("");
        setBusy(false);
        return;
      }
      const codeMessages = {
        invalid_credentials: t("jamedaConnect.errorInvalidCredentials"),
        not_found: t("jamedaConnect.errorNotFound"),
        server_error: t("jamedaConnect.errorUnreachable"),
        unreachable: t("jamedaConnect.errorUnreachable"),
      };
      setError(codeMessages[code] || t("jamedaConnect.errorGeneric"));
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={() => (!busy ? onClose?.() : null)} labelledBy={titleId}>
      <button
        type="button"
        onClick={() => (!busy ? onClose?.() : null)}
        className="absolute right-5 top-5 rounded-lg p-2 text-[#64748b] transition hover:bg-[#f1f5f9] hover:text-[#0f172a]"
        aria-label={t("common.close")}
      >
        <MaterialIcon name="close" className="text-[20px]" />
      </button>

      <div className="px-8 pb-2 pt-10 md:px-10">
        <div className="flex items-center gap-4">
          <JamedaLogo className="h-12 w-12" />
          <div>
            <h2 id={titleId} className="text-[22px] font-bold tracking-tight text-[#0f172a]">
              {t("jamedaConnect.title")}
            </h2>
            <p className="mt-1 text-[14px] leading-relaxed text-[#6b7280]">
              {t("jamedaConnect.subtitle")}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 px-8 py-6 md:grid-cols-[1.05fr_1fr] md:px-10">
        {/* Guide: where to find the credentials */}
        <div className="rounded-2xl border border-[#e5e7eb] bg-[#f8fafc] p-5">
          <p className="mb-4 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#006666]">
            {t("jamedaConnect.guideTitle")}
          </p>
          <ol className="space-y-4">
            {GUIDE_STEP_KEYS.map((step, index) => (
              <li key={step.titleKey} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#006666] text-[12px] font-bold text-white">
                  {index + 1}
                </span>
                <div className="min-w-0">
                  <p className="text-[13.5px] font-semibold text-[#0f172a]">
                    {t(step.titleKey)}
                  </p>
                  <p className="mt-0.5 text-[12.5px] leading-[1.55] text-[#64748b]">
                    {t(step.detailKey)}
                  </p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-5 flex items-start gap-2 rounded-xl bg-white px-3.5 py-3 ring-1 ring-[#e5e7eb]">
            <MaterialIcon name="support_agent" className="mt-0.5 shrink-0 text-[16px] text-[#006666]" />
            <p className="text-[12px] leading-relaxed text-[#64748b]">
              {t("jamedaConnect.helpNote")}{" "}
              <Link
                to="/dashboard/client/support"
                onClick={onClose}
                className="font-semibold text-[#006666] hover:underline"
              >
                {t("jamedaConnect.helpLink")}
              </Link>
            </p>
          </div>
        </div>

        {/* Credentials form */}
        <form onSubmit={handleSubmit} className="flex flex-col">
          <label className="block">
            <span className="text-[13px] font-semibold text-[#0f172a]">
              {t("jamedaConnect.businessIdLabel")}
            </span>
            <input
              type="text"
              value={businessId}
              onChange={(event) => setBusinessId(event.target.value)}
              placeholder={t("jamedaConnect.businessIdPlaceholder")}
              autoComplete="off"
              className="mt-1.5 w-full rounded-xl border border-[#e5e7eb] bg-white px-4 py-3 text-[14px] outline-none transition focus:border-[#006666] focus:ring-2 focus:ring-[#006666]/20"
            />
            <span className="mt-1.5 block text-[12px] text-[#94a3b8]">
              {t("jamedaConnect.businessIdHint")}
            </span>
          </label>

          <label className="mt-4 block">
            <span className="text-[13px] font-semibold text-[#0f172a]">
              {t("jamedaConnect.apiKeyLabel")}
            </span>
            <div className="relative mt-1.5">
              <input
                type={showKey ? "text" : "password"}
                value={apiKey}
                onChange={(event) => setApiKey(event.target.value)}
                placeholder={t("jamedaConnect.apiKeyPlaceholder")}
                autoComplete="off"
                className="w-full rounded-xl border border-[#e5e7eb] bg-white px-4 py-3 pr-11 text-[14px] outline-none transition focus:border-[#006666] focus:ring-2 focus:ring-[#006666]/20"
              />
              <button
                type="button"
                onClick={() => setShowKey((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] transition hover:text-[#0f172a]"
                aria-label={showKey ? t("jamedaConnect.hideKey") : t("jamedaConnect.showKey")}
              >
                <MaterialIcon name={showKey ? "lock" : "key"} className="text-[18px]" />
              </button>
            </div>
            <span className="mt-1.5 block text-[12px] text-[#94a3b8]">
              {t("jamedaConnect.apiKeyHint")}
            </span>
          </label>

          {limitHit ? (
            <div className="mt-4 rounded-xl border border-[#0f172a]/10 bg-[#0f172a] px-4 py-3.5 text-white">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/60">
                {t("platforms.limit.eyebrow")}
              </p>
              <p className="mt-1 text-[13px] leading-relaxed text-white/90">
                {t("platforms.limit.message")}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => {
                    onClose?.();
                    requestMoreProfiles("jameda");
                  }}
                  className="rounded-full bg-white px-4 py-1.5 text-[12.5px] font-bold text-[#0f172a] transition hover:bg-[#f0fafa]"
                >
                  {t("platforms.limit.contactSupport")}
                </button>
                <button
                  type="button"
                  onClick={() => setLimitHit(false)}
                  className="rounded-full px-3 py-1.5 text-[12.5px] font-semibold text-white/80 transition hover:text-white"
                >
                  {t("platforms.limit.jamedaTryAnother")}
                </button>
              </div>
            </div>
          ) : null}
          {error ? (
            <p className="mt-4 rounded-xl bg-error-container px-4 py-3 text-[13px] text-on-error-container">
              {error}
            </p>
          ) : null}

          <div className="mt-auto flex items-center justify-end gap-3 pt-6">
            <Button
              type="button"
              variant="ghost"
              className="!h-auto !w-auto shrink-0 px-3 py-2"
              disabled={busy}
              onClick={onClose}
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="!h-12 !w-auto min-w-[190px] shrink-0 rounded-full px-8 !text-[15px]"
              disabled={busy || !businessId.trim() || !apiKey.trim()}
            >
              {busy ? t("jamedaConnect.connecting") : t("jamedaConnect.connectCta")}
            </Button>
          </div>

          <p className="mt-4 flex items-start gap-1.5 text-[11.5px] leading-relaxed text-[#94a3b8]">
            <MaterialIcon name="lock" className="mt-0.5 shrink-0 text-[13px]" />
            {t("jamedaConnect.securityNote")}
          </p>
        </form>
      </div>
    </Modal>
  );
};

export default JamedaConnectModal;
